/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AccountingAccountDto,
  AccountingMigrationAccountDto,
  AccountingMigrationDataDto,
  AccountingMigrationGstCategoryDto,
  TaxRateDto,
  XeroAccountCreateRequestDto,
} from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  entityComplementor,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  NumberTypes,
  setEntityField,
  setFieldError,
  TextDictionary,
} from '@gh/common-shared';
import { ID, WithEntityState } from '@gh/common-shared-data';
import { validationError } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { countBy, flatten, groupBy, isEmpty, isNil, keyBy, omit, pick, pickBy, sortBy } from 'lodash';

export type AccMigrationAccount = WithEntityState<{
  accountId: ID;
  code: string;
  name: string;
  xeroAccount?: AccMigrationXeroAccount;
  isMandatory: boolean;
  isSynced: boolean;
}>;
export type AccMigrationGstCategory = WithEntityState<{
  gstCategoryId: ID;
  code: string;
  name: string;
  ratePct?: number;
  xeroTaxRate?: AccMigrationXeroTaxRate;
  isSynced: boolean;
  isActive: boolean;
}>;
export type AccMigrationXeroAccountDetails = XeroAccountCreateRequestDto;
export type AccMigrationXeroAccount = AccountingAccountDto & { isGroup?: boolean };
export type AccMigrationXeroTaxRate = TaxRateDto;

export enum AccMigrationStep { Accounts = 'Accounts', Categories = 'Categories' }

export type AccMigrationDetails = {
  step: AccMigrationStep;
  accounts: AccMigrationAccount[];
  categories: AccMigrationGstCategory[];
  xeroTaxRates: AccMigrationXeroTaxRate[];
  accountsFocusedCell?: FocusedCell;
  categoriesFocusedCell?: FocusedCell;
};

export const ACC_MIGRATION_DETAILS_INIT: AccMigrationDetails = {
  step: AccMigrationStep.Accounts,
  accounts: [],
  categories: [],
  xeroTaxRates: [],
};

export const getAccMigrationAccountId = (account: AccMigrationAccount) => account.accountId;
export const stringifyAccountName = ({ code, name }: AccMigrationAccount) => `${code || ''} ${name || ''}`;
export const stringifyXeroAccountName = ({ isGroup, code, name }: AccMigrationXeroAccount) =>
  isGroup ? name : `${code || ''} ${name || ''}`;
export const accMigrationAccountArr = createArrayHelper(getAccMigrationAccountId);

const ACC_MIGRATION_ACCOUNT_ENTITY_CONFIG: EntityConfig<AccMigrationAccount> = {
  xeroAccount: {
    editable: (account) => !account.isSynced,
    required: (account) => !account.isSynced && account.isMandatory,
  },
};

export const accMigrationAccountFieldState = fieldStateByEntityConfig(ACC_MIGRATION_ACCOUNT_ENTITY_CONFIG);
export const validateAccMigrationAccountField = fieldValidatorByEntityConfig(ACC_MIGRATION_ACCOUNT_ENTITY_CONFIG);
export const validateAccMigrationAccount = entityValidatorByEntityConfig(ACC_MIGRATION_ACCOUNT_ENTITY_CONFIG);
export const complementAccMigrationAccount = entityComplementor(validateAccMigrationAccount);
export const setAccMigrationAccountField = composeSetters(
  setEntityField,
  fieldValidationSetter(validateAccMigrationAccountField));

export const getAccMigrationGstCategoryId = (gst: AccMigrationGstCategory) => gst.gstCategoryId;
export const stringifyTaxRateName = (taxRate: AccMigrationXeroTaxRate) =>
  `${taxRate.name} ${NumberTypes.Rate.format(taxRate.taxRate)}%`;
export const accMigrationGstCategoryArr = createArrayHelper(getAccMigrationGstCategoryId);

const ACC_MIGRATION_GST_CATEGORY_ENTITY_CONFIG: EntityConfig<AccMigrationGstCategory> = {
  xeroTaxRate: {
    required: (category) => !category.isSynced,
    editable: (category) => !category.isSynced,
    validator: ({ xeroTaxRate, ratePct }) => {
      if (isNil(xeroTaxRate)) {
        return void 0;
      }
      return ratePct !== xeroTaxRate.taxRate ? { rate: validationError('msg_map_gst_not_equal_rate') } : void 0;
    },
  },
};

export const accMigrationGstCategoryFieldState = fieldStateByEntityConfig(ACC_MIGRATION_GST_CATEGORY_ENTITY_CONFIG);
export const validateAccMigrationGstCategoryField = fieldValidatorByEntityConfig(
  ACC_MIGRATION_GST_CATEGORY_ENTITY_CONFIG);
export const validateAccMigrationGstCategory = entityValidatorByEntityConfig(ACC_MIGRATION_GST_CATEGORY_ENTITY_CONFIG);
export const complementAccMigrationGstCategory = entityComplementor(validateAccMigrationGstCategory);
export const setAccMigrationGstCategoryField = composeSetters(
  setEntityField,
  fieldValidationSetter(validateAccMigrationGstCategoryField));

export const accMigrationDetailsFromDto = (dto: AccountingMigrationDataDto): AccMigrationDetails => ({
  step: AccMigrationStep.Accounts,
  accounts: sortBy(dto.accounts.map(accMigrationAccountFromDto), 'code'),
  categories: dto.gstCategories.map(accMigrationCategoryFromDto),
  xeroTaxRates: [],
});

export const accMigrationDetailsToDto = (model: AccMigrationDetails): AccountingMigrationDataDto => ({
  accounts: model.accounts.map((account) => {
    const { xeroAccount } = account;
    return {
      ...pick(account, ['accountId', 'code', 'name', 'isMandatory']),
      externalId: xeroAccount ? xeroAccount.externalId : void 0,
      externalCode: xeroAccount ? xeroAccount.code : void 0,
      externalName: xeroAccount ? xeroAccount.name : void 0,
      isBankAccount: xeroAccount ? xeroAccount.isBankAccount : false,
    };
  }),
  gstCategories: model.categories.map((category) => {
    const { xeroTaxRate } = category;
    return {
      ...<AccMigrationGstCategory>pick(
        category,
        ['gstCategoryId', 'code', 'name', 'ratePct', 'isMandatory', 'isActive']),
      taxType: xeroTaxRate ? xeroTaxRate.taxType : void 0,
      taxName: xeroTaxRate ? xeroTaxRate.name : void 0,
      taxRate: xeroTaxRate ? xeroTaxRate.taxRate : void 0,
    };
  }),
});

export const accMigrationAccountFromDto = (dto: AccountingMigrationAccountDto): AccMigrationAccount =>
  complementAccMigrationAccount(<AccMigrationAccount>({
    ...omit(dto, 'externalId', 'externalCode', 'externalName'),
    xeroAccount: dto.externalId && {
      externalId: dto.externalId,
      code: dto.externalCode,
      name: dto.externalName,
      isBankAccount: dto.isBankAccount,
    },
    isSynced: isNotNil(dto.externalId),
  }));

export const accMigrationXeroAccountListFromDto = (accountClasses: TextDictionary,
                                                   accounts: AccountingAccountDto[]): AccMigrationXeroAccount[] => {
  const accountsByClass = groupBy(accounts, ({ accountClass }) => accountClass);
  return flatten(accountClasses.seq().map(({ id, name }) => {
    const accountsForClass = accountsByClass[id];
    if (isEmpty(accountsForClass)) {
      return [];
    }
    return (<AccMigrationXeroAccount[]>[{ externalId: id, code: '', name, isGroup: true }])
      .concat(accountsForClass.map((account) => ({ ...account, isGroup: false })));
  }));
};

export const accMigrationCategoryFromDto = (dto: AccountingMigrationGstCategoryDto): AccMigrationGstCategory =>
  complementAccMigrationGstCategory(<AccMigrationGstCategory>({
    ...omit(dto, 'taxName', 'taxRate', 'taxType'),
    xeroTaxRate: dto.taxType && {
      name: dto.taxName,
      taxRate: dto.taxRate,
      taxType: dto.taxType,
    },
    isSynced: isNotNil(dto.taxType) && dto.ratePct === dto.taxRate,
  }));

export const validateUniqueXeroAccounts = (accounts: AccMigrationAccount[]): AccMigrationAccount[] => {
  const countOfXeroAccounts = countBy(
    accounts.filter(({ isSynced, xeroAccount }) => !isSynced && xeroAccount),
    // tslint:disable-next-line:no-non-null-assertion
    ({ xeroAccount }) => xeroAccount!.externalId);
  const notUniqueXeroAccounts = pickBy(countOfXeroAccounts, (count) => count > 1);

  const isXeroAccountNotUnique = (xeroAccount: AccMigrationXeroAccount) =>
    isNotNil(notUniqueXeroAccounts[xeroAccount.externalId]);

  const validateUniqueXeroAccount = (account: AccMigrationAccount) =>
    setFieldError(
      account,
      'xeroAccount',
      'unique',
      account.xeroAccount
        ? isXeroAccountNotUnique(account.xeroAccount) ? validationError('msg_map_accounts_not_unique') : void 0
        : void 0);

  return accMigrationAccountArr.updateAll(accounts, validateUniqueXeroAccount);
};
